import { AfterViewInit, ChangeDetectorRef, Component, HostListener, LOCALE_ID, OnInit, Renderer2, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { OrderComponent } from './order/order.component';
import { MatDialog } from '@angular/material/dialog';
import { Invoice, InvoiceStatus, Order } from '../models/order.model';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { OrderService } from '../services/order.service';
import { TableColumnPickerComponent } from '../table-column-picker/table-column-picker.component';
import { ColumnOption } from '../models/column-option';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { ConsultantService } from '../services/consultant.service';
import { DepartmentService } from '../services/department.service';
import { Consultant, ExecutorAmount } from '../models/consultant.model';
import { Department } from '../models/department.model';
import { InvoiceComponent } from '../invoices/invoice/invoice.component';
import { MatSort } from '@angular/material/sort';
import { InvoiceService } from '../services/invoice.service';
import { OrderFilter, OrderFilterComponent } from './order-filter/order-filter.component';
import { AuthService } from '../services/auth.service';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { PagedResult } from '../models/paged-result.model';
import { PaginatorIntl } from '../PaginatorIntl';
import { ListOrdersModel } from './types/list-orders.model';

const COLUMN_ORDER_KEY = "orderColumnOrder";

@Component({
    selector: 'app-orders',
    templateUrl: './orders.component.html',
    styleUrls: ['./orders.component.scss'],
    // providers: [DatePipe, {provide: LOCALE_ID, useValue: 'da-DK'}] 
    providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrdersComponent implements OnInit, AfterViewInit {

  orders: ListOrdersModel[] = [];
  loading = true;

  // Guards against the initial programmatic sort in ngOnInit triggering an extra reload.
  private sortReady = false;

  @ViewChild(OrderFilterComponent) orderFilter: OrderFilterComponent;
  @ViewChild(MatTable<ListOrdersModel>) table: MatTable<ListOrdersModel>;
  @ViewChild(MatSort) sorter: MatSort;
  dataSource: MatTableDataSource<ListOrdersModel> = new MatTableDataSource([]);
  displayedColumns = [
    'actions',
    'taskNumber',
    'year',
    'consultantId',
    'customerAndTask',
    'projectSumExVat',
    'pipelineProjectSumExVat',
    'pipelinePercentForSuccess',
    'pipelineWeightedProjectSum',
    'leadCreatorId',
    'salesRealizators',
    'executors',
    'expectedInvoice',
    'distribution',
    'comment',
    'invoicingFinished',
    'departmentId',
    'hubspotDeal',
  ];
  // datePipe = new DatePipe('da-DK');

  editing: Order;
  editingProperty: string;

  previousIndex: number;

  consultants: Consultant[];
  departments: Department[];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  page: PagedResult<ListOrdersModel>;
  pageEvent: PageEvent;

  containerHeight = "500px";

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // Update viewport dimensions on window resize event
    this.getTableHeight();
  }

  distributionTotal(row: ListOrdersModel): number {
    const arr = row.realizedInvoices || [];
    return arr.reduce((sum, i) => sum + (i?.amount || 0), 0);
  }

  getTableHeight() {
    let subtract = 64;
    this.containerHeight = (window.innerHeight - subtract) + "px";
  }

  constructor(
    private cd: ChangeDetectorRef,
    public dialog: MatDialog,
    public ordersService: OrderService,
    public consultantsService: ConsultantService,
    public departmentsService: DepartmentService,
    public invoiceService: InvoiceService,
    protected auth: AuthService,
  ) {

    // if (!localStorage.getItem(COLUMN_ORDER_KEY)) {
    //   let columns = new Order();
    //   localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(this.displayedColumns));
    // } else {
    //   this.displayedColumns = JSON.parse(localStorage.getItem(COLUMN_ORDER_KEY));
    // }

  }
  ngAfterViewInit(): void {

  }

  async ngOnInit(): Promise<void> {
    this.getTableHeight();
    this.consultants = await this.consultantsService.list();
    this.departments = await this.departmentsService.list();

    this.dataSource.filterPredicate = (order, filter) => {
      let f = filter?.toLocaleLowerCase();
      if (order.comment?.toLowerCase()?.includes(f)) return true;
      if (order.consultant?.initials?.toLowerCase()?.includes(f)) return true;
      if (order.customerAndTask?.toLowerCase()?.includes(f)) return true;
      if (order.departmentName?.toLowerCase()?.includes(f)) return true;
      if (order.economicCustomerNumber?.toLowerCase()?.includes(f)) return true;
      if (order.leadCreator?.initials?.toLowerCase()?.includes(f)) return true;
      if (order.salesRealizators?.some(_ => _.name.toLocaleLowerCase().includes(f) || _.initials.toLocaleLowerCase().includes(f))) return true;
      if (order.executors?.some(_ => _.name.toLocaleLowerCase().includes(f) || _.initials.toLocaleLowerCase().includes(f))) return true;
      if (order.taskNumber?.toLowerCase()?.includes(f)) return true;
      return false;
    };

    this.sorter.sort({ disableClear: false, id: "created", start: "desc" });

    await this.loadOrders();
    this.sortReady = true;
  }

  // Sorting is handled server-side: a header click reloads the current filter/page
  // from the API with the new sort, resetting to the first page.
  onSortChange(): void {
    if (!this.sortReady) return;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    const page: PageEvent = {
      pageIndex: 0,
      pageSize: this.pageEvent?.pageSize ?? this.paginator?.pageSize ?? 50,
      length: this.page?.totalCount ?? 0,
    };
    this.loadOrders(page, this.orderFilter?.lastEmitted);
  }

  public async loadOrders(page?: PageEvent, filters?: OrderFilter) {
    this.paginator.initialized.subscribe(async () => {
      this.loading = true;
      this.orders = [];
      this.dataSource.data = [];
      if (page == null) {
        page = this.pageEvent;
      } else {
        this.pageEvent = page;
      }

      if (filters == null) {
        filters = this.orderFilter?.lastEmitted;
      }

      if (!filters?.owner) {
        const activeEmail = this.auth.activeAccount()?.username;
        const me = this.consultants.find(_ => _.email === activeEmail);
        if (me) {
          filters = {
            ...filters,
            owner: [me.id]
          };
        }
      }

      const items = (await this.ordersService.list(
        page?.pageIndex ?? 0, page?.pageSize ?? 50,
        this.sorter?.active, this.sorter?.direction,
        filters?.owner, filters?.department, filters?.text,
        filters?.type, filters?.finishedInvoicing, filters?.year));

      this.orders = items.items;

      // this.orderFilter.emitLast(true);
      this.page = items;
      this.dataSource.data = this.orders;
      this.loading = false;
    });
  }
  invoiceStatus(order: Order) {
    // let invoice = this.invoices.find(_ => _.orderId == order.id);

    // if (invoice?.status == InvoiceStatus.SentToEconomic) {
    //   return "green";
    // }

    return "inherit";
  }

  async createInvoice(row: ListOrdersModel) {
    const order = await this.ordersService.get(row.id);
    let dialogRef = this.dialog.open(InvoiceComponent, {
      data: { order },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async createdInvoice => {
      if (createdInvoice) {
        console.log("invoice created");
        await this.loadOrders();
      }
    });
  }

  async deleteOrder(row: ListOrdersModel) {
    if (window.confirm("Er du sikker på at du vil slette: " + row.customerAndTask + "?"))
      if (await this.ordersService.delete(row.id)) {
        await this.loadOrders();
      } else {
        window.alert("Denne ordre kan ikke slettes");
      }
  }

  createOrder() {
    let dialogRef = this.dialog.open(OrderComponent, {
      // width: 'fit-content',
      data: { order: new Order() },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async createdOrder => {
      if (createdOrder) {
        await this.loadOrders();
      }
    });
  }

  async openOrder(row: ListOrdersModel) {
    const order = await this.ordersService.get(row.id);

    let dialogRef = this.dialog.open(OrderComponent, {
      // width: 'fit-content',
      data: { order },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async updatedOrder => {
      if (updatedOrder) {
        await this.loadOrders();
      }
    });
  }

  dropListDropped(event: CdkDragDrop<any, any>) {
    if (event) {
      moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(this.displayedColumns));
      this.table.renderRows();
    }
  }

  async exportAllToExcel() {
    let file = await this.ordersService.exportAllToExcel();
    const blob = new Blob([file.body], { type: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;` });
    const link = document.createElement('a');
    if (link.download !== undefined) { // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `ordrebog_export.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // shouldShowColumn(input: string): boolean {
  //   let columnPicker = new TableColumnPickerComponent;
  //   return columnPicker.formControl.value.includes(input);
  // }

  displayedColumnsChanger(event: string[]) {
    setTimeout(() => {
      this.displayedColumns = ["actions", ...event];
    });
  }

  // filter(filter: OrderFilter) {
  //   let orders = this.orders;

  //   // if (filter) {
  //   //   if (filter.owner?.length > 0) {
  //   //     orders = [...orders.filter(_ => filter.owner.find(__ => _.consultantId == __))];
  //   //   }

  //   //   if (filter.department?.length > 0) {
  //   //     orders = [...orders.filter(_ => filter.department.find(__ => _.departmentId == __))];
  //   //   }

  //   //   if (filter.owner?.length == 0 && filter.department?.length == 0)
  //   //     orders = this.orders;

  //   //   switch (filter.type) {
  //   //     case "ORDERS":
  //   //       orders = [...orders.filter(_ => _.projectSumExVat)];
  //   //       break;
  //   //     case "PIPELINE":
  //   //       orders = [...orders.filter(_ => _.pipelineProjectSumExVat)];
  //   //       break;
  //   //   }

  //   //   switch (filter.finishedInvoicing) {
  //   //     case "DONE":
  //   //       orders = [...orders.filter(_ => _.invoicingFinished)];
  //   //       break;
  //   //     case "NOT_DONE":
  //   //       orders = [...orders.filter(_ => !_.invoicingFinished)];
  //   //       break;
  //   //   }
  //   // }

  //   // this.dataSource.data = orders;
  //   // this.dataSource.filter = filter?.text;
  // }
}

